import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CircleCheck, LoaderCircle, LogIn } from 'lucide-react';
import type { SessionInfo } from '@ana/contracts';
import { api, errorMessage } from '../api/client.ts';
import { useSession } from '../auth/session.tsx';
import { PasswordInput } from '../components/PasswordInput.tsx';
import { AuthShell, FormError, restoreFocus } from './auth/AuthShell.tsx';

/** Navigation state left by the set-password page. */
interface LoginNotice {
  passwordSet?: boolean;
  email?: string;
}

export function LoginPage() {
  const { apply } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [notice] = useState(() => location.state as LoginNotice | null);
  const noticeId = useId();
  const passwordId = useId();
  const [identifier, setIdentifier] = useState(notice?.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);

  // The notice is news of the arrival from the set-password page: drop it from the history entry, or a reload, or
  // this screen shown again after "Sair" (same entry), would repeat it with the e-mail filled in.
  useEffect(() => {
    if ((location.state as LoginNotice | null)?.passwordSet) {
      void navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    }
  }, [location, navigate]);

  const signIn = async (login: string, secret: string) => {
    setBusy(true);
    setError(null);
    try {
      apply(
        await api<SessionInfo>('/auth/login', {
          method: 'POST',
          body: { email: login.trim(), password: secret },
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
      setAttempt((n) => n + 1);
      restoreFocus(document.getElementById(passwordId));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void signIn(identifier, password);
  };

  return (
    <AuthShell>
      <form className="auth-enter" onSubmit={submit} aria-labelledby="login-title">
        <h2 id="login-title">Acesse sua conta</h2>
        <p className="muted small">Informe o e-mail ou o usuário e a senha de acesso.</p>
        {notice?.passwordSet ? (
          <div className="alert ok" role="status" id={noticeId}>
            <CircleCheck size={18} aria-hidden="true" />
            <span>Senha definida. Entre com o seu e-mail e a nova senha.</span>
          </div>
        ) : null}
        <FormError error={error} attempt={attempt} />
        <label className="field">
          E-mail ou usuário
          <input
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            inputMode="email"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </label>
        {/* With the notice the focus starts here, and a status present on arrival is not announced: the field
            carries it as its description instead. */}
        <PasswordInput
          id={passwordId}
          label="Senha"
          autoComplete="current-password"
          required
          autoFocus={Boolean(notice?.passwordSet)}
          aria-describedby={notice?.passwordSet ? noticeId : undefined}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button className="btn primary block" type="submit" disabled={busy}>
          {busy ? (
            <LoaderCircle size={18} className="spin" aria-hidden="true" />
          ) : (
            <LogIn size={18} aria-hidden="true" />
          )}
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
        <p className="help">
          <Link
            to="/esqueci-a-senha"
            state={identifier.includes('@') ? { email: identifier.trim() } : undefined}
          >
            Esqueci minha senha
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

/** Second factor, MFA enrolment and forced password change (auth gates). */
export function GatePage() {
  const { session, apply, logout, refresh } = useSession();
  const [code, setCode] = useState('');
  const [secret, setSecret] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!session) return null;
  const gate = session.gates[0];

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div>
        <h2>
          {gate === 'PENDING_MFA'
            ? 'Verificação em duas etapas'
            : gate === 'MFA_SETUP_REQUIRED'
              ? 'Ative a verificação em duas etapas'
              : 'Troque sua senha'}
        </h2>
        <FormError error={error} />
        {gate === 'PENDING_MFA' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () =>
                apply(await api<SessionInfo>('/auth/mfa/verify', { method: 'POST', body: { code } })),
              );
            }}
          >
            <label className="field">
              Código do aplicativo autenticador
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            <div className="actions" style={{ marginTop: '1rem' }}>
              <button className="btn primary" disabled={busy}>
                Verificar
              </button>
              <button type="button" className="btn" onClick={() => void logout()}>
                Cancelar
              </button>
            </div>
          </form>
        ) : null}
        {gate === 'MFA_SETUP_REQUIRED' ? (
          <div>
            <p className="small">
              Seu perfil exige verificação em duas etapas. Cadastre a chave abaixo em um aplicativo
              autenticador (Google Authenticator, Microsoft Authenticator ou similar).
            </p>
            {!secret ? (
              <button
                className="btn primary"
                disabled={busy}
                onClick={() =>
                  void run(async () => setSecret(await api('/auth/mfa/setup', { method: 'POST' })))
                }
              >
                Gerar chave
              </button>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(async () =>
                    apply(await api<SessionInfo>('/auth/mfa/enable', { method: 'POST', body: { code } })),
                  );
                }}
              >
                <p className="mono" data-testid="mfa-secret">
                  {secret.secret}
                </p>
                <p className="mono small muted">{secret.otpauthUri}</p>
                <label className="field">
                  Código gerado pelo aplicativo
                  <input
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </label>
                <div className="actions" style={{ marginTop: '1rem' }}>
                  <button className="btn primary" disabled={busy}>
                    Ativar
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : null}
        {gate === 'MUST_CHANGE_PASSWORD' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await api('/auth/password', {
                  method: 'POST',
                  body: { currentPassword: current, newPassword: next },
                });
                await refresh();
              });
            }}
          >
            <PasswordInput
              label="Senha atual"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
            <PasswordInput
              label="Nova senha (mínimo 12 caracteres)"
              autoComplete="new-password"
              minLength={12}
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <div className="actions" style={{ marginTop: '1rem' }}>
              <button className="btn primary" disabled={busy}>
                Salvar nova senha
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </AuthShell>
  );
}
